import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useParams, useLocation } from 'react-router-dom';

import Section from './components/Section';
import { useMount } from 'utils/hooks';
import useUpdateOnly from 'hooks/useUpdateOnly';

import classes from './Drawer.module.scss';

const Drawer = ({ hideDrawer, menuOptions = [], setContentRendered }) => {
  const history = useHistory();
  const { bu, lang, section } = useParams();
  const { pathname } = useLocation();

  const [activeName, setActiveName] = useState(null);
  const [expandedTabsMap, setExpandedTabsMap] = useState({});

  const hasMenuOptions = Array.isArray(menuOptions) && menuOptions.length > 0;

  const collapseAllTabs = useCallback(() => {
    setExpandedTabsMap(prev => {
      if (!prev || !Object.keys(prev).length) return prev;

      const next = {};
      for (const tabName of Object.keys(prev)) {
        next[tabName] = { ...prev[tabName], expanded: false };
      }
      return next;
    });
  }, []);

  const setOnlyTabExpanded = useCallback(tabNameToExpand => {
    setExpandedTabsMap(prev => {
      const next = {};
      for (const tabName of Object.keys(prev)) {
        next[tabName] = {
          ...prev[tabName],
          expanded:
            tabName === tabNameToExpand ? !prev[tabName]?.expanded : false
        };
      }

      return next;
    });
  }, []);

  // Determine active parent section from URL (either direct section, or parent of a child page)
  useMount(() => {
    if (!hasMenuOptions || !section) return;

    const isMainSection = menuOptions.some(s => s?.name === section);
    if (isMainSection) {
      setActiveName(section);
      return;
    }

    const parent = menuOptions.find(sec =>
      sec?.pages?.some(child => child?.name === section)
    );

    if (parent?.name) setActiveName(parent.name);
  });

  // Auto-expand when user first navigates to a link (main section OR child section)
  useMount(() => {
    if (!hasMenuOptions || !section) return;

    const match = menuOptions.find(item => {
      if (!item) return false;
      if (item.name === section) return true;
      return item?.pages?.some(p => p?.name === section);
    });

    if (match?.name) setOnlyTabExpanded(match.name);
  });

  // Build/refresh the expandable tabs map when activeName or menuOptions change
  useEffect(() => {
    if (!hasMenuOptions) return;

    setExpandedTabsMap(() => {
      return menuOptions.reduce((acc, item) => {
        if (!item?.name) return acc;

        // Only sections that have children are expandable in the map
        if (Array.isArray(item.pages) && item.pages.length > 0) {
          acc[item.name] = {
            name: item.name,
            expanded: item.name === activeName
          };
        }

        return acc;
      }, {});
    });
  }, [activeName, hasMenuOptions, menuOptions]);

  useUpdateOnly(() => {
    const hasDrawer = expandedTabsMap[activeName];

    if (!hasDrawer) return;

    const hasCloseDrawer = expandedTabsMap[activeName]?.expanded;
    if (hasCloseDrawer) return;

    setOnlyTabExpanded(activeName);
  }, [pathname]);

  const onClickSelectSection = useCallback(
    ({ name, pages, isSubMenu = false }) => {
      if (!name) return;

      setActiveName(name);

      // If this is a leaf main section (not submenu, no pages) collapse everything and stop.
      if (!isSubMenu && (!pages || pages.length === 0)) {
        collapseAllTabs();
        return;
      }

      // Expand/collapse only if this click is on a top-level menu (not submenu)
      if (!isSubMenu) setOnlyTabExpanded(name);

      history.push(`/${bu}/${name}/${lang}`);
    },
    [bu, collapseAllTabs, history, lang, setOnlyTabExpanded]
  );

  const tree = useMemo(() => {
    if (!hasMenuOptions) return null;

    return (
      <ul className={classes.Tree}>
        {menuOptions.map(item => (
          <Section
            key={item?.name ?? item?.title}
            item={item}
            setContentRendered={setContentRendered}
            onClickSelectSection={onClickSelectSection}
            expandedTabsMap={expandedTabsMap}
            hideDrawer={hideDrawer}
            activeName={activeName}
          />
        ))}
      </ul>
    );
  }, [
    activeName,
    expandedTabsMap,
    hasMenuOptions,
    hideDrawer,
    menuOptions,
    onClickSelectSection,
    setContentRendered
  ]);

  if (!hasMenuOptions) return null;

  return (
    <aside className={classes.Container}>
      <div className={classes.NavTree}>{tree}</div>
    </aside>
  );
};

export default Drawer;
